import {
  computed, ref, watch,
} from '@vue/composition-api'
import {
  getDaysInMonth, getMonth, setDate, setMonth, startOfDay,
} from 'date-fns'
import { cloneDeep } from 'lodash'

export default function useWorkplaceDueDateRules() {
  const workplaceDueDateRules = ref({
    workplace_id: null,
    workplace_name: null,
    due_day: null,
    rules: [],
  })
  const workplaceDueDateRulesSimulations = ref([])
  const dueDateRulesSubmitted = ref(false)

  const daysOfMonthOptions = ref(new Array(31).fill().map((_, day) => ({ text: day + 1, value: day + 1 })))
  const addMonthOptions = ref(new Array(6).fill().map((_, day) => ({ text: day + 1, value: day + 1 })))

  const tableColumns = [
    {
      key: 'start_day',
      label: 'Dia inicial (inclusive)',
    },
    {
      key: 'end_day',
      label: 'Dia final (inclusive)',
    },
    {
      key: 'months_to_add',
      label: 'Meses a somar',
    },
    {
      key: 'result',
      label: 'Resultado',
    },
    {
      key: 'actions',
      label: 'Ações',
    },
  ]

  const tableSimulatorColumns = [
    {
      key: 'duty_date',
      label: 'Data do serviço (plantão)',
    },
    {
      key: 'due_day',
      label: 'Dia do mês',
    },
    {
      key: 'day_range',
      label: 'Faixa aplicada',
    },
    {
      key: 'months_to_add',
      label: 'Meses a somar',
    },
    {
      key: 'date',
      label: 'Data para faturar/receber',
    },
  ]

  function getRandomIntInclusive(min, max) {
    // eslint-disable-next-line no-param-reassign
    min = Math.ceil(min)
    // eslint-disable-next-line no-param-reassign
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  function getSafeBillingDate(targetDay) {
    const baseDate = new Date()
    const maxDays = getDaysInMonth(baseDate)
    const safeDay = Math.min(targetDay, maxDays)
    return startOfDay(setDate(baseDate, safeDay))
  }

  const isValid = rule => rule.start_day && rule.end_day && rule.months_to_add

  const generateSimulations = () => {
    const dueDay = workplaceDueDateRules.value.due_day
    if (!dueDay) return

    workplaceDueDateRulesSimulations.value = workplaceDueDateRules.value.rules.filter(isValid).map(rule => {
      const day = getRandomIntInclusive(rule.start_day, rule.end_day)
      const dutyDate = getSafeBillingDate(day)
      const date = setDate(setMonth(dutyDate, getMonth(dutyDate) + rule.months_to_add), dueDay)
      return {
        duty_date: dutyDate,
        due_day: dueDay,
        day_range: { start: rule.start_day, end: rule.end_day },
        months_to_add: rule.months_to_add,
        date,
      }
    })
  }

  watch(workplaceDueDateRules, () => {
    generateSimulations()
  }, { deep: true })

  function loadWorkplace(workplace) {
    workplaceDueDateRules.value.workplace_id = workplace.id
    workplaceDueDateRules.value.workplace_name = workplace.name
    const dueRule = workplace.due_date_rules && workplace.due_date_rules[0]
    workplaceDueDateRules.value.due_day = dueRule?.due_day ?? null
    workplaceDueDateRules.value.rules = (workplace.due_date_rules || []).map(rule => ({
      start_day: rule.start_day,
      end_day: rule.end_day,
      months_to_add: rule.months_to_add,
    }))
    dueDateRulesSubmitted.value = false
    generateSimulations()
  }

  function addDueDateRule() {
    workplaceDueDateRules.value.rules.push({
      start_day: null,
      end_day: null,
      months_to_add: null,
    })
  }

  function deleteRule(item) {
    const index = workplaceDueDateRules.value.rules.indexOf(item)
    workplaceDueDateRules.value.rules.splice(index, 1)
  }

  function hasOverlappingDayIntervals(rules) {
    const intervals = rules
      .filter(rule => rule.start_day && rule.end_day)
      .map(rule => [Number(rule.start_day), Number(rule.end_day)])
      .filter(([start, end]) => start <= end)

    const sorted = [...intervals].sort((a, b) => a[0] - b[0])

    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i][0] <= sorted[i - 1][1]) {
        return true
      }
    }
    return false
  }

  const hasIntervalOverlap = computed(() => hasOverlappingDayIntervals(workplaceDueDateRules.value.rules))

  const hasIncompleteRules = computed(() => workplaceDueDateRules.value.rules.some(rule => {
    const hasAny = rule.start_day || rule.end_day || rule.months_to_add
    const hasAll = rule.start_day && rule.end_day && rule.months_to_add
    return hasAny && !hasAll
  }))

  function dueDaySelectState() {
    if (!dueDateRulesSubmitted.value) return null
    return workplaceDueDateRules.value.due_day ? null : false
  }

  function ruleFieldFeedback(rule, field) {
    if (dueDateRulesSubmitted.value && !rule[field]) {
      return 'Campo obrigatório'
    }
    if ((field === 'start_day' || field === 'end_day') && hasIntervalOverlap.value) {
      return 'Há sobreposição entre faixas de dias'
    }
    return ''
  }

  function ruleSelectState(rule, field) {
    return ruleFieldFeedback(rule, field) ? false : null
  }

  function validateDueDateRulesBeforeSave() {
    dueDateRulesSubmitted.value = true

    if (!workplaceDueDateRules.value.due_day) {
      return 'Informe o dia fixo para faturar/receber.'
    }
    if (!workplaceDueDateRules.value.rules.length) {
      return 'Adicione ao menos uma faixa de dias.'
    }
    if (hasIncompleteRules.value) {
      return 'Preencha dia inicial, dia final e meses a somar em todas as faixas.'
    }
    if (hasIntervalOverlap.value) {
      return 'Há sobreposição entre faixas de dias no mesmo mês.'
    }
    if (!workplaceDueDateRules.value.rules.every(isValid)) {
      return 'Preencha todos os campos das faixas.'
    }
    return null
  }

  function getPayload() {
    const day = workplaceDueDateRules.value.due_day
    return {
      workplace_id: workplaceDueDateRules.value.workplace_id,
      due_date_rules: workplaceDueDateRules.value.rules.filter(isValid).map(rule => ({
        start_day: rule.start_day,
        end_day: rule.end_day,
        due_day: day,
        months_to_add: rule.months_to_add,
      })),
    }
  }

  const disableGreater = (options, value) => {
    if (!value) return options
    return cloneDeep(options).map(item => ({ ...item, disabled: item.value >= value }))
  }

  const disableLess = (options, value) => {
    if (!value) return options
    return cloneDeep(options).map(item => ({ ...item, disabled: item.value <= value }))
  }

  return {
    workplaceDueDateRules,
    workplaceDueDateRulesSimulations,
    daysOfMonthOptions,
    addMonthOptions,
    tableColumns,
    tableSimulatorColumns,
    dueDaySelectState,
    ruleSelectState,
    ruleFieldFeedback,
    loadWorkplace,
    validateDueDateRulesBeforeSave,
    getPayload,
    addDueDateRule,
    deleteRule,
    disableLess,
    disableGreater,
  }
}
